import { useEffect, useRef, useState } from 'react';
import { chargerBinaire } from '../api';

/** Un CV dépasse rarement quelques pages ; au-delà, le téléchargement prend le relais. */
const PAGES_MAX = 12;

type Etat = { phase: 'chargement' } | { phase: 'erreur' } | { phase: 'pret'; affichees: number; total: number };

/**
 * Aperçu PDF dessiné page par page dans la page, avec pdf.js. Un PDF dans un
 * cadre ne s'affiche pas sur les téléphones Android, qui sont l'usage premier
 * de l'application. pdf.js n'exécute pas le JavaScript embarqué d'un PDF, et
 * l'évaluation dynamique est coupée : le document reste une donnée.
 */
export function ApercuPdf({ documentId }: { documentId: string }) {
  const conteneur = useRef<HTMLDivElement>(null);
  const [etat, setEtat] = useState<Etat>({ phase: 'chargement' });

  useEffect(() => {
    const cible = conteneur.current;
    if (!cible) return;
    let annule = false;
    let liberer: (() => Promise<void>) | null = null;
    cible.replaceChildren();
    setEtat({ phase: 'chargement' });

    (async () => {
      // Chargé à la demande : pdf.js ne pèse rien tant qu'on n'ouvre pas de CV.
      const [pdfjs, travailleur, donnees] = await Promise.all([
        import('pdfjs-dist/legacy/build/pdf.mjs'),
        import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?url'),
        chargerBinaire(documentId),
      ]);
      pdfjs.GlobalWorkerOptions.workerSrc = travailleur.default;
      const tache = pdfjs.getDocument({ data: donnees, isEvalSupported: false, enableXfa: false });
      liberer = () => tache.destroy();
      const pdf = await tache.promise;

      const total = Math.min(pdf.numPages, PAGES_MAX);
      const largeur = cible.clientWidth || 600;
      const densite = Math.min(window.devicePixelRatio || 1, 2);
      for (let numero = 1; numero <= total && !annule; numero += 1) {
        const page = await pdf.getPage(numero);
        const echelle = (largeur / page.getViewport({ scale: 1 }).width) * densite;
        const vue = page.getViewport({ scale: echelle });
        const toile = window.document.createElement('canvas');
        toile.width = Math.floor(vue.width);
        toile.height = Math.floor(vue.height);
        toile.className = 'page-pdf';
        toile.setAttribute('role', 'img');
        toile.setAttribute('aria-label', `Page ${numero}`);
        cible.append(toile);
        const contexte = toile.getContext('2d');
        if (!contexte) throw new Error('Canvas indisponible');
        await page.render({ canvasContext: contexte, viewport: vue }).promise;
      }
      if (!annule) setEtat({ phase: 'pret', affichees: total, total: pdf.numPages });
    })().catch(() => {
      if (!annule) setEtat({ phase: 'erreur' });
    });

    return () => {
      annule = true;
      void liberer?.();
    };
  }, [documentId]);

  return (
    <div className="apercu-pdf">
      {etat.phase === 'chargement' && <p className="attente">Chargement du document…</p>}
      {etat.phase === 'erreur' && (
        <p className="erreur">
          Aperçu impossible : lire le texte extrait ou télécharger le fichier.
        </p>
      )}
      <div ref={conteneur} className="pages-pdf" />
      {etat.phase === 'pret' && etat.total > etat.affichees && (
        <p className="mention">
          {etat.affichees} premières pages sur {etat.total} : télécharger le fichier pour la suite.
        </p>
      )}
    </div>
  );
}
